import { RefObject, useEffect, useState, useCallback } from 'react';

interface LoopedTabulationOptions {
  containerRef: RefObject<HTMLDivElement>;
  isAssignmentPanelOpen?: boolean;
  selectors: string;
  excludeIntercomButton: boolean;
}

export const useModalLoopedTabulation = ({
  containerRef,
  isAssignmentPanelOpen = false,
  selectors = '',
  excludeIntercomButton = false,
}: LoopedTabulationOptions) => {
  const [intercomButton, setIntercomButton] = useState<HTMLElement>();
  const [iframeEvent, setIframeEvent] = useState<KeyboardEvent>();

  const removeHiddenAssignmentPanelElements = (
    container: HTMLElement,
    containerButtons: HTMLButtonElement[],
  ) => {
    const calendar = container.querySelector('.calendarIsOpen');

    if (!!calendar) {
      return containerButtons;
    }

    return containerButtons.filter(
      (button) =>
        !button.classList.contains('close-date-panel-btn') &&
        !button.classList.contains('open-date-panel-btn'),
    );
  };

  const getAssignmentPanelCalendarInputs = (container: HTMLElement) => {
    const inputs: HTMLElement[] = [];

    const assignPanelHourInput: HTMLElement | null =
      container.querySelector('.time-picker-hour');

    if (assignPanelHourInput) {
      inputs.push(assignPanelHourInput);
    }

    const assignPanelMinuteInput: HTMLElement | null = container.querySelector(
      '.time-picker-minute',
    );

    if (assignPanelMinuteInput) {
      inputs.push(assignPanelMinuteInput);
    }

    return inputs;
  };

  const getAssignmentPanelRadioOptions = (container: HTMLElement) => {
    const assignPanelRadioOptions = container.querySelectorAll(
      '.assign-panel-radio-option input',
    ) as NodeListOf<HTMLElement>;

    if (!assignPanelRadioOptions.length) {
      return [];
    }

    return Array.from(assignPanelRadioOptions);
  };

  const loopAssignmentPanelCalendarTabulation = (
    container: HTMLElement,
    keyboardEvent: KeyboardEvent,
  ) => {
    const { activeElement } = document;

    const lastCalendarElement: HTMLElement | null = container.querySelector(
      '.calendarIsOpen .time-picker-pm',
    );

    if (lastCalendarElement === activeElement) {
      const firstCalendarElement: HTMLElement | null = container.querySelector(
        '.calendarIsOpen .close-date-panel-btn',
      );

      if (firstCalendarElement) {
        keyboardEvent.preventDefault();
        firstCalendarElement.focus();
      }
    }
  };

  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];

    const containerButtons = Array.from(
      containerRef.current.querySelectorAll('button'),
    );

    const filteredButtons = isAssignmentPanelOpen
      ? removeHiddenAssignmentPanelElements(
          containerRef.current,
          containerButtons,
        )
      : containerButtons;

    const customElements: HTMLElement[] = selectors
      ? Array.from(containerRef.current.querySelectorAll(selectors))
      : [];

    const focusableElements: HTMLElement[] = selectors
      ? customElements
      : [...filteredButtons, ...containerRef.current.querySelectorAll('a')];

    const overviewVideoCheckbox = document.getElementById(
      'overview-video-checkbox',
    );

    if (overviewVideoCheckbox) {
      focusableElements.push(overviewVideoCheckbox);
    }

    const additionalFocusableElements = containerRef.current.querySelectorAll(
      'div[tabindex]',
    ) as NodeListOf<HTMLElement>;

    if (additionalFocusableElements.length) {
      focusableElements.push(...additionalFocusableElements);
    }

    if (isAssignmentPanelOpen) {
      focusableElements.push(
        ...getAssignmentPanelCalendarInputs(containerRef.current),
        ...getAssignmentPanelRadioOptions(containerRef.current),
      );
    }

    if (intercomButton) focusableElements.push(intercomButton);

    return focusableElements;
  }, [containerRef, intercomButton, isAssignmentPanelOpen, selectors]);

  const handleKeyPress = (e: KeyboardEvent) => {
    const { activeElement } = document;

    if (e.key !== 'Tab') return;

    const focusableElements = getFocusableElements();

    const firstFocusableElement = focusableElements[0] as
      | HTMLElement
      | undefined;
    const lastFocusableElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement | undefined;

    if (intercomButton && !document.contains(intercomButton)) {
      findIntercomButton();
    }

    if (isAssignmentPanelOpen && containerRef.current) {
      loopAssignmentPanelCalendarTabulation(containerRef.current, e);
    }

    // if activeElement is not from focusableElement
    if (
      !activeElement?.classList.contains('intercom-launcher-frame') &&
      !focusableElements.includes(activeElement as HTMLElement)
    ) {
      // focus first element
      e.preventDefault();
      firstFocusableElement?.focus();
    }

    if (e.shiftKey) {
      if (activeElement === firstFocusableElement) {
        // focus last element
        e.preventDefault();
        lastFocusableElement?.focus();
      }

      return;
    }

    // if activeElement is intercom button
    if (
      activeElement === lastFocusableElement ||
      activeElement?.classList.contains('intercom-launcher-frame')
    ) {
      // focus first element
      e.preventDefault();
      firstFocusableElement?.focus();
    }
  };

  const handleIframeWindowKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Tab') setIframeEvent(e);
  };

  const findIntercomButton = () => {
    let button;

    const intercomIframe = document.querySelector('.intercom-launcher-frame');

    if (intercomIframe) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const iframeWindow = intercomIframe.contentWindow;

      iframeWindow.addEventListener('keydown', handleIframeWindowKeyPress);

      button = iframeWindow.document.querySelector('.intercom-launcher');
    }

    if (!button) {
      button = document.querySelector('.intercom-lightweight-app-launcher');
    }

    if (button) {
      setIntercomButton(button as HTMLElement);
    }
  };

  // initial receiving of intercomButton
  useEffect(() => {
    if (intercomButton || excludeIntercomButton) return;

    const timer = setTimeout(findIntercomButton, 2000);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intercomButton, excludeIntercomButton]);

  // initial setup
  useEffect(() => {
    const focusableElements = getFocusableElements();

    document.body.addEventListener('keydown', handleKeyPress);
    focusableElements.forEach((item) => item.setAttribute('tabIndex', '0'));

    return () => {
      document.body.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intercomButton]);

  // handle keyboard events from intercom iframe
  useEffect(() => {
    if (iframeEvent) handleKeyPress(iframeEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeEvent]);

  // clear intercom iframe window event listener
  useEffect(() => {
    const intercomIframe = document.querySelector('.intercom-launcher-frame');

    if (intercomIframe) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const iframeWindow = intercomIframe.contentWindow;

      iframeWindow.removeEventListener('keydown', handleIframeWindowKeyPress);
    }
  }, []);
};
